import { fetchOrganizations } from 'admin/actions/organization';
import { getPrivateMoneys } from 'admin/actions/private-moneys';
import { fetchShopAccounts, fetchShops } from 'admin/actions/shops';
import { Button } from 'admin/components/atoms/Buttons';
import DateTime from 'admin/components/atoms/DateTime';
import DateTimeRange from 'admin/components/atoms/DateTimeRange';
import Label from 'admin/components/atoms/Label';
import Select from 'admin/components/atoms/Select';
import TextBox from 'admin/components/atoms/TextBox';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAppSelector } from '../../../hooks/useApp';
import { dateTimeView, endDateTimeFormat } from '../../../utils';
import FormErrorMessage from '../../atoms/FormErrorMessage';
import Grid from '../../atoms/Grid';
import LocalizedMask from '../../atoms/LocalizedMask';
import Required from '../../atoms/Required';
import TextArea from '../../atoms/TextArea';
import Tooltip from '../../atoms/Tooltip';

type Props = {
  onSubmit: (data: MoneyAccountHookForm) => void;
  mode: 'create' | 'edit';
  moneyAccountHookForm?: MoneyAccountHookForm & {
    id?: string;
    is_disabled?: boolean;
    usage_count?: number;
  };
};

type Option<T> = {
  label: string;
  value: T;
};

export type MoneyAccountHookForm = {
  privateMoney?: {
    id: string;
    name?: string;
  };
  senderOrganization?: {
    code?: string;
    name?: string;
  } | null;
  senderShop?: {
    id?: string;
    name?: string;
  } | null;
  senderAccount: {
    id?: string;
    name?: string;
  } | null;
  bearPointOrganization?: {
    code?: string;
    name?: string;
  } | null;
  bearPointShop?: {
    id?: string;
    name?: string;
  } | null;
  bearPointAccount?: {
    id?: string;
    name?: string;
  } | null;
  pointAmount: number;
  pointExpiresAt?: Moment;
  pointExpiresInDays?: number;
  dateRange?: {
    from: Moment | null;
    to: Moment | null;
  };
  usageLimit?: number;
  name: string;
  description?: string;
};

const defaultFormValues: Partial<MoneyAccountHookForm> = {
  senderOrganization: null,
  senderShop: null,
  bearPointOrganization: null,
  bearPointShop: null,
  dateRange: {
    from: null,
    to: null,
  },
  pointAmount: 0,
};

const FormGrid = styled(Grid.Container)`
  margin: 16px 0;

  ${Select as any} {
    line-height: 1.5;
  }

  ${Label} {
    margin-bottom: 0;
    padding: 5px 0;
    color: #676767;
  }

  ${DateTime} {
    margin-bottom: 0;
  }

  ${DateTimeRange} {
    width: 100%;
    margin-bottom: 0;

    div:has(> ${DateTime}) {
      flex: 1;
    }
  }
`;

const EditMoneyAccountHook = ({ onSubmit, mode, moneyAccountHookForm }: Props) => {
  const user = useAppSelector((state) => state.user);
  const defaultMoney = user.defaultMoney();

  const {
    register,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<MoneyAccountHookForm>({
    defaultValues: {
      privateMoney:
        mode === 'create'
          ? {
              id: defaultMoney?.id ?? '',
              name: defaultMoney?.name ?? '',
            }
          : undefined,
      ...(moneyAccountHookForm || defaultFormValues),
    },
  });

  register('senderAccount', { required: '送金元店舗を指定してください' });

  const [privateMoneys, setPrivateMoneys] = useState<
    Option<NonNullable<MoneyAccountHookForm['privateMoney']>>[]
  >([]);
  const [organizations, setOrganizations] = useState<
    Option<NonNullable<MoneyAccountHookForm['senderOrganization']>>[]
  >([]);
  const [senderShops, setSenderShops] = useState<
    Option<NonNullable<MoneyAccountHookForm['senderShop']>>[]
  >([]);
  const [pointBearerShops, setPointBearerShops] = useState<
    Option<NonNullable<MoneyAccountHookForm['bearPointShop']>>[]
  >([]);

  const getOrganizations = useCallback((privateMoneyId: string) => {
    fetchOrganizations(`private_money_id=${privateMoneyId}&per_page=10000`).then((orgs) => {
      setOrganizations(orgs.rows?.map((org) => ({ label: org.name ?? '', value: org })) ?? []);
    });
  }, []);

  const getShops = useCallback((organizationCode: string, type: 'sender' | 'bearer') => {
    const selectedPrivateMoney = getValues('privateMoney');
    if (!selectedPrivateMoney) {
      return;
    }
    fetchShops(
      `organization_code=${organizationCode}&private_money_id=${selectedPrivateMoney.id}`
    ).then((s) => {
      if (type === 'sender') {
        setSenderShops(s?.map((shop) => ({ label: shop.name ?? '', value: shop })) ?? []);
      } else {
        setPointBearerShops(s?.map((shop) => ({ label: shop.name ?? '', value: shop })) ?? []);
      }
    });
  }, []);

  const setShopAccount = useCallback((shopId: string, type: 'sender' | 'bearer') => {
    const selectedPrivateMoney = getValues('privateMoney');
    if (!selectedPrivateMoney) {
      return;
    }
    fetchShopAccounts(shopId, `private_money_id=${selectedPrivateMoney.id}`).then((accounts) => {
      if (type === 'sender') {
        setValue('senderAccount', accounts ? accounts[0] : null);
      } else {
        setValue('bearPointAccount', accounts ? accounts[0] : null);
      }
    });
  }, []);

  useEffect(() => {
    if (user.isServiceUser()) {
      getPrivateMoneys('per_page=2000').then((moneys) => {
        setPrivateMoneys(
          moneys.rows?.map((money) => ({ label: money.name ?? '', value: money })) ?? []
        );
      });
    } else {
      setPrivateMoneys(
        user.issuedMoneys().map((money) => ({ label: money.name ?? '', value: money })) ?? []
      );
    }
    if (defaultMoney) {
      getOrganizations(defaultMoney.id);
    }
  }, []);

  useEffect(() => {
    if (mode === 'edit' && moneyAccountHookForm) {
      reset(moneyAccountHookForm);
    }
  }, [moneyAccountHookForm, mode]);

  return (
    <FormGrid>
      {mode === 'create' && (
        <>
          <Grid.Item size={2}>
            <Label>
              マネー <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={6} flexDirection="column">
            <Controller
              name="privateMoney"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  isMulti={false}
                  fullWidth
                  options={privateMoneys}
                  value={value ? { label: value.name ?? '', value } : null}
                  onChange={(option) => {
                    onChange(option?.value ?? null);
                    setValue('senderOrganization', null);
                    setValue('senderShop', null);
                    setValue('senderAccount', null);
                    setValue('bearPointOrganization', null);
                    setValue('bearPointShop', null);
                    setValue('bearPointAccount', null);
                    setSenderShops([]);
                    setPointBearerShops([]);

                    if (option) {
                      getOrganizations(option?.value.id);
                    }
                  }}
                  placeholder="マネーを選択してください。"
                />
              )}
            />
            <FormErrorMessage name="privateMoney" errors={errors} />
          </Grid.Item>

          <Grid.Divider color="transparent" />

          <Grid.Item size={2}>
            <Label>
              送金元店舗 <Required />
            </Label>
          </Grid.Item>
          <Grid.Item flexDirection="column" size={6} container gridSize={1}>
            <Grid.Item container columnGap={24} gridSize={2}>
              <Grid.Item>
                <Controller
                  name="senderOrganization"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      isMulti={false}
                      fullWidth
                      options={organizations}
                      value={value ? { label: value.name ?? '', value } : null}
                      onChange={(option) => {
                        onChange(option?.value ?? null);
                        setValue('senderShop', null);
                        setValue('senderAccount', null);
                        setSenderShops([]);
                        if (option?.value?.code) {
                          getShops(option.value.code, 'sender');
                        }
                      }}
                      placeholder="「組織」を選択"
                    />
                  )}
                />
              </Grid.Item>
              <Grid.Item>
                <Controller
                  name="senderShop"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      isMulti={false}
                      fullWidth
                      options={senderShops}
                      value={value ? { label: value.name ?? '', value } : null}
                      onChange={(option) => {
                        onChange(option?.value ?? null);
                        if (option?.value?.id) {
                          setShopAccount(option.value.id, 'sender');
                        }
                      }}
                      placeholder="「店舗」を選択"
                    />
                  )}
                />
              </Grid.Item>
            </Grid.Item>
            <FormErrorMessage name="senderAccount" errors={errors} />
          </Grid.Item>

          <Grid.Divider />
        </>
      )}

      {mode === 'edit' && (
        <>
          <Grid.Item size={2}>
            <Label>状態</Label>
          </Grid.Item>
          <Grid.Item size={6}>
            <Label>{moneyAccountHookForm?.is_disabled ?? false ? '無効' : '有効'}</Label>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>ID</Label>
          </Grid.Item>
          <Grid.Item size={6}>
            <Label>{moneyAccountHookForm?.id ?? ''}</Label>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <Label>送金元店舗</Label>
          </Grid.Item>
          <Grid.Item size={6}>
            <Link to={`/shops/${moneyAccountHookForm?.senderShop?.id}`}>
              <div style={{ marginTop: 5, marginBottom: 15, wordBreak: 'break-word' }}>
                {moneyAccountHookForm?.senderShop?.name}
              </div>
            </Link>
          </Grid.Item>

          <Grid.Divider />
        </>
      )}
      <Grid.Item size={2}>
        <Label>
          キャンペーン名 <Required />
          <Tooltip id="helpCampaignName" text="お客様の利用履歴に表示します" />
        </Label>
      </Grid.Item>
      <Grid.Item size={6} flexDirection="column">
        <TextArea
          fullWidth
          {...register('name', { required: 'キャンペーン名を入力してください' })}
        />
        <FormErrorMessage name="name" errors={errors} />
      </Grid.Item>

      <Grid.Divider color="transparent" />

      <Grid.Item size={2}>
        <Label>
          説明
          <Tooltip id="helpCampaignDescription" text="最大200文字までです" />
        </Label>
      </Grid.Item>

      <Grid.Item size={6}>
        <TextArea fullWidth {...register('description')} />
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>対象期間</Label>
      </Grid.Item>

      <Grid.Item size={6}>
        <Controller
          name="dateRange"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DateTimeRange
              dateTimeProps={{
                fromProps: {
                  showIcon: true,
                },
                toProps: {
                  showIcon: true,
                },
              }}
              dateRange={{
                from: value?.from ?? null,
                to: value ? dateTimeView(value.to) : null,
              }}
              setDateRange={(range) =>
                onChange({
                  from: range.from ? moment(range.from).hours(0) : null,
                  to: range.to ? endDateTimeFormat(range.to) : null,
                })
              }
              seperatorProps={{
                styles: { width: '16px', textAlign: 'center' },
              }}
            />
          )}
        />
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>
          ポイント金額 <Required />
        </Label>
      </Grid.Item>

      <Grid.Item size={6} flexDirection="column">
        <Controller
          name="pointAmount"
          control={control}
          rules={{
            required: '1以上の整数を入力してください',
            validate: (value) => (value < 1 ? '1以上の整数を入力してください' : true),
          }}
          render={({ field: { value, onChange } }) => (
            <LocalizedMask value={value.toString()} onChange={onChange}>
              {({ value, ...inputProps }) => <TextBox {...inputProps} syncValue={value} />}
            </LocalizedMask>
          )}
        />
        <FormErrorMessage name="pointAmount" errors={errors} />
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>ポイント有効期限</Label>
      </Grid.Item>

      <Grid.Item size={6} container gridSize={2} columnGap={24} rowGap={8}>
        <Grid.Item flexDirection="column">
          <Label>日時指定</Label>
          <Controller
            name="pointExpiresAt"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DateTime
                showIcon
                dateFormat="YYYY/MM/DD HH:mm:ss"
                timeFormat={false}
                value={dateTimeView(value) ?? undefined}
                onChange={(date) => onChange(endDateTimeFormat(date))}
                isValidDate={(current) => moment(current).isSameOrAfter(moment().startOf('day'))}
              />
            )}
          />
        </Grid.Item>
        <Grid.Item flexDirection="column">
          <Label>相対日数指定</Label>
          <TextBox {...register('pointExpiresInDays')} fullWidth type="number" min={0} />
          <span style={{ color: '#757575', marginTop: '0.25rem' }}>
            0にすると当日の24時までになります
          </span>
        </Grid.Item>
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>
          ポイント負担先店舗
          {mode === 'create' && (
            <Tooltip
              id="helpPointBearerOrgShop"
              text={
                <span>
                  ポイント利用分をマネー発行体に支払う店舗を指定します。
                  <br />
                  未指定時は送金元店舗が負担します。
                </span>
              }
            />
          )}
        </Label>
      </Grid.Item>

      {mode === 'create' && (
        <Grid.Item size={6} container columnGap={24} gridSize={2}>
          <Grid.Item>
            <Controller
              name="bearPointOrganization"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  isMulti={false}
                  fullWidth
                  options={organizations}
                  value={value ? { label: value.name ?? '', value } : null}
                  onChange={(option) => {
                    onChange(option?.value ?? null);
                    setValue('bearPointShop', null);
                    setValue('bearPointAccount', null);
                    setPointBearerShops([]);
                    if (option?.value?.code) {
                      getShops(option.value.code, 'bearer');
                    }
                  }}
                  placeholder="「組織」を選択"
                />
              )}
            />
          </Grid.Item>
          <Grid.Item>
            <Controller
              name="bearPointShop"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  isMulti={false}
                  fullWidth
                  options={pointBearerShops}
                  value={value ? { label: value.name ?? '', value } : null}
                  onChange={(option) => {
                    onChange(option?.value ?? null);
                    if (option?.value?.id) {
                      setShopAccount(option.value.id, 'bearer');
                    }
                  }}
                  placeholder="「店舗」を選択"
                />
              )}
            />
          </Grid.Item>
        </Grid.Item>
      )}

      {mode === 'edit' && (
        <Grid.Item size={6}>
          {moneyAccountHookForm?.bearPointAccount?.id ? (
            <Link to={`/shops/${moneyAccountHookForm?.bearPointAccount.id}`}>
              <div style={{ marginTop: 5, marginBottom: 15, wordBreak: 'break-word' }}>
                {moneyAccountHookForm?.bearPointAccount?.name}
              </div>
            </Link>
          ) : (
            <Link to={`/shops/${moneyAccountHookForm?.senderShop?.id}`}>
              <div style={{ marginTop: 5, marginBottom: 15, wordBreak: 'break-word' }}>
                {moneyAccountHookForm?.senderShop?.name}
              </div>
            </Link>
          )}
        </Grid.Item>
      )}

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>付与上限人数</Label>
      </Grid.Item>

      <Grid.Item size={6} flexDirection="column">
        <Controller
          name="usageLimit"
          control={control}
          render={({ field: { value, onChange } }) => (
            <LocalizedMask value={value?.toString() ?? ''} onChange={onChange}>
              {({ value: syncValue, ...inputProps }) => (
                <TextBox {...inputProps} syncValue={syncValue} />
              )}
            </LocalizedMask>
          )}
        />
        {mode === 'edit' && (moneyAccountHookForm?.usage_count ?? undefined) !== undefined && (
          <span style={{ color: '#848484', fontSize: '14px', marginTop: '8px' }}>
            付与済み人数 ：{moneyAccountHookForm?.usage_count?.toLocaleString()}
          </span>
        )}
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={12} nextRow>
        <Button onClick={handleSubmit(onSubmit)}>{mode === 'create' ? '登録' : '更新'}</Button>
      </Grid.Item>
    </FormGrid>
  );
};

export default EditMoneyAccountHook;
